import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import settingsService, { SiteSettings, SiteContent } from '../services/settingsService';

interface SettingsContextType {
  settings: Partial<SiteSettings>;
  content: SiteContent;
  loading: boolean;
  error: string | null;
  refreshSettings: () => Promise<void>;
  getSettingValue: (category: string, key: string, defaultValue?: any) => any;
  getContentValue: (category: string, key: string, defaultValue?: string) => string;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<Partial<SiteSettings>>({});
  const [content, setContent] = useState<SiteContent>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    try {
      setError(null);

      // Get current language from i18n or localStorage
      const currentLanguage = localStorage.getItem('i18nextLng') || 'en';

      // Fetch settings and content in parallel
      const [settingsData, contentData] = await Promise.all([
        settingsService.getPublicSettings(),
        settingsService.getPublicContent(undefined, currentLanguage)
      ]);

      setSettings(settingsData);
      setContent(contentData);
    } catch (err) {
      // Error loading settings
      setError(err instanceof Error ? err.message : 'Failed to load settings');

      // Set fallback values
      setSettings({
        general: {
          company_name: 'Patrix Global',
          primary_color: '#3B82F6',
          secondary_color: '#1E40AF',
          logo_url: '/images/logo.png',
          favicon_url: '/favicon.ico',
          currency: 'USD'
        },
        contact: {
          main_email: 'info@patrixglobal.com',
          main_phone: '+1 (800) 123-4567',
          whatsapp_number: '+1234567890',
          support_email: 'support@patrixglobal.com',
          offices: {}
        },
        statistics: {
          companies_formed: 10000,
          countries_served: 15,
          client_satisfaction: 98,
          years_experience: 10,
          support_availability: '24/7'
        },
        social: {
          facebook_url: '',
          linkedin_url: '',
          twitter_url: '',
          instagram_url: ''
        },
        footer: {
          footer_enabled: true,
          company_section_enabled: true,
          services_section_enabled: true,
          quicklinks_section_enabled: true,
          contact_section_enabled: true,
          social_links_enabled: true,
          bottom_bar_enabled: true,
          wave_separator_enabled: true,
          animations_enabled: true,
          company_description_en: 'Your trusted partner for global business formation.',
          company_description_ar: 'شريكك الموثوق في تأسيس الأعمال العالمية.',
          company_logo_enabled: true,
          services_heading_en: 'Our Services',
          services_heading_ar: 'خدماتنا',
          quicklinks_heading_en: 'Quick Links',
          quicklinks_heading_ar: 'روابط سريعة',
          contact_heading_en: 'Contact Info',
          contact_heading_ar: 'معلومات التواصل',
          footer_email: 'info@patrixglobal.com',
          footer_support_hours_en: '24/7 Available',
          footer_support_hours_ar: 'متاح 24/7',
          whatsapp_contact_enabled: true,
          copyright_text_en: 'All rights reserved.',
          copyright_text_ar: 'جميع الحقوق محفوظة.',
          show_legal_links: true,
          background_color: '#111827',
          primary_color: '#2563eb',
          heading_color: '#ffffff',
          text_color: '#d1d5db',
          link_color: '#d1d5db',
          link_hover_color: '#3b82f6',
          icon_color: '#d1d5db',
          icon_background_color: '#374151',
          wave_color: '#ffffff',
          divider_color: '#374151',
          background_gradient_start: '#111827',
          background_gradient_end: '#1F2937',
          text_primary_color: '#FFFFFF',
          text_secondary_color: '#D1D5DB',
          display_all_countries: true,
          selected_countries: ['saudi-arabia', 'egypt', 'uk', 'usa'],
          show_twitter: true,
          show_linkedin: true,
          show_facebook: true,
          show_whatsapp_social: true
        }
      });

      setContent({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const refreshSettings = async () => {
    setLoading(true);
    await loadData();
  };

  const getSettingValue = (category: string, key: string, defaultValue: any = null): any => {
    const categorySettings = settings[category as keyof SiteSettings];
    if (categorySettings && typeof categorySettings === 'object') {
      return (categorySettings as any)[key] !== undefined ? (categorySettings as any)[key] : defaultValue;
    }
    return defaultValue;
  };

  const getContentValue = (category: string, key: string, defaultValue: string = ''): string => {
    const categoryContent = content[category];
    if (categoryContent) {
      return categoryContent[key] || defaultValue;
    }
    return defaultValue;
  };

  const value: SettingsContextType = {
    settings,
    content,
    loading,
    error,
    refreshSettings,
    getSettingValue,
    getContentValue
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

// Custom hooks for specific settings
export const useCompanyName = (): string => {
  const { getSettingValue } = useSettings();
  return getSettingValue('general', 'company_name', 'Patrix Global');
};

export const useContactInfo = () => {
  const { settings } = useSettings();
  return settings.contact || {
    main_email: 'info@patrixglobal.com',
    main_phone: '+1 (800) 123-4567',
    whatsapp_number: '+1234567890',
    support_email: 'support@patrixglobal.com',
    offices: {}
  };
};

export const useStatistics = () => {
  const { settings } = useSettings();
  return settings.statistics || {
    companies_formed: 10000,
    countries_served: 15,
    client_satisfaction: 98,
    years_experience: 10,
    support_availability: '24/7'
  };
};

export const useWhatsAppNumber = (): string => {
  const { getSettingValue } = useSettings();
  return getSettingValue('contact', 'whatsapp_number', '+1234567890');
};

export const useOffices = () => {
  const { getSettingValue } = useSettings();
  return getSettingValue('contact', 'offices', {});
};

export const useFooterSettings = () => {
  const { settings } = useSettings();
  return settings.footer || {
    footer_enabled: true,
    company_section_enabled: true,
    services_section_enabled: true,
    quicklinks_section_enabled: true,
    contact_section_enabled: true,
    social_links_enabled: true,
    bottom_bar_enabled: true,
    wave_separator_enabled: true,
    animations_enabled: true,
    company_description_en: 'Your trusted partner for global business formation.',
    company_description_ar: 'شريكك الموثوق في تأسيس الأعمال العالمية.',
    company_logo_enabled: true,
    services_heading_en: 'Our Services',
    services_heading_ar: 'خدماتنا',
    quicklinks_heading_en: 'Quick Links',
    quicklinks_heading_ar: 'روابط سريعة',
    contact_heading_en: 'Contact Info',
    contact_heading_ar: 'معلومات التواصل',
    footer_email: 'info@patrixglobal.com',
    footer_support_hours_en: '24/7 Available',
    footer_support_hours_ar: 'متاح 24/7',
    whatsapp_contact_enabled: true,
    copyright_text_en: 'All rights reserved.',
    copyright_text_ar: 'جميع الحقوق محفوظة.',
    show_legal_links: true,
    background_color: '#111827',
    primary_color: '#2563eb',
    heading_color: '#ffffff',
    text_color: '#d1d5db',
    link_color: '#d1d5db',
    link_hover_color: '#3b82f6',
    icon_color: '#d1d5db',
    icon_background_color: '#374151',
    wave_color: '#ffffff',
    divider_color: '#374151',
    background_gradient_start: '#111827',
    background_gradient_end: '#1F2937',
    text_primary_color: '#FFFFFF',
    text_secondary_color: '#D1D5DB',
    display_all_countries: true,
    selected_countries: ['saudi-arabia', 'egypt', 'uk', 'usa'],
    show_twitter: true,
    show_linkedin: true,
    show_facebook: true,
    show_whatsapp_social: true
  };
};

export const useSocialLinks = () => {
  const { settings } = useSettings();
  return settings.social || {
    facebook_url: '',
    linkedin_url: '',
    twitter_url: '',
    instagram_url: ''
  };
};